/**
 * El aviso.
 *
 * Arregla un fallo que estaba vivo en producción: dos pantallas pintaban sus mensajes más serios
 * —dominio de correo no permitido, cuenta con el acceso retirado, error al administrar— con
 * clases de un tono de aviso que **no estaba definido en ningún sitio**. El relleno no generaba
 * ninguna regla y el borde caía en el color del texto, así que el aviso se veía como una caja
 * vacía con un filete claro. Justo donde había que entender qué había pasado.
 *
 * `scripts/check-clases-css.sh` existe para que eso no vuelva a colarse.
 *
 * Ahora es una pegatina de relleno sólido. El motivo de que el aviso lleve fondo y el éxito no,
 * es medible: en escala de grises el verde de «correcto» y el ámbar de «atención» quedan a
 * 1,05:1, o sea, indistinguibles. Se separan por forma —uno es una pegatina rellena, el otro un
 * trazo— antes que por color.
 */

import { Glifo, type NombreGlifo } from './glifo.js';

export type TonoAviso = 'atencion' | 'error' | 'exito';

const TONOS: Record<TonoAviso, { readonly clases: string; readonly glifo: NombreGlifo }> = {
  // Relleno sólido: es lo que lo hace visible de un vistazo y en escala de grises.
  atencion: { clases: 'bg-warn text-on-warn border-edge', glifo: 'aviso' },
  error: { clases: 'bg-surface text-text border-ko', glifo: 'incorrecto' },
  exito: { clases: 'bg-surface text-text border-ok', glifo: 'correcto' },
};

export interface AvisoProps {
  readonly tono?: TonoAviso;
  /**
   * Si interrumpe lo que se estaba haciendo.
   *
   * `alert` corta al lector de pantalla para contarlo; `status` espera su turno. Un error que
   * impide seguir es lo primero; una confirmación de guardado, no.
   *
   * **No se pone dentro de una región `aria-live`.** Si un nodo con rol de región viva se inserta
   * dentro de otra, manda la del ancestro, así que un `alert` metido en una cola cortés deja de
   * interrumpir y algunos lectores lo leen dos veces. Los avisos efímeros de `avisos.tsx` viven
   * dentro de dos regiones hermanas y por eso pasan `interrumpe={undefined}`: el rol lo pone la
   * región, no la pegatina.
   */
  readonly interrumpe?: boolean | undefined;
  readonly className?: string;
  /** Lo que va al final de la caja: un botón de cerrar, un enlace para reintentar. */
  readonly accion?: React.ReactNode;
  readonly children: React.ReactNode;
}

export function Aviso({
  tono = 'atencion',
  interrumpe,
  className = '',
  accion,
  children,
}: AvisoProps): React.JSX.Element {
  const { clases, glifo } = TONOS[tono];
  return (
    <div
      // Sin `interrumpe` no lleva rol ninguno: el anuncio lo hace quien lo contenga.
      {...(interrumpe === undefined ? {} : { role: interrumpe ? 'alert' : 'status' })}
      className={[
        'rounded-card flex items-start gap-3 border-2 px-4 py-3 text-left',
        clases,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Glifo nombre={glifo} size={22} className="mt-0.5" />
      <div className="min-w-0 flex-1">{children}</div>
      {accion}
    </div>
  );
}
